import React from "react";
import { ArrowRight, Dumbbell } from "lucide-react";
import { LOGO_URL, IMAGES } from "../data/site";

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16"
        >
            {/* background image */}
            <div
                className="absolute inset-0 z-0"
                style={{
                    backgroundImage: `url(${IMAGES.hero})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            />
            {/* overlays */}
            <div className="absolute inset-0 z-10 bg-gradient-to-br from-ink via-ink/85 to-ink/60" />
            <div className="absolute inset-0 z-10 bg-ink/55" />
            <div className="absolute inset-0 z-10 bg-grid opacity-60" />
            <div className="absolute inset-0 z-10 noise pointer-events-none" />

            {/* gold light streaks */}
            <div
                className="gold-streak z-20"
                style={{ top: "18%", left: "-10%", width: "60%", transform: "rotate(-12deg)" }}
            />
            <div
                className="gold-streak z-20"
                style={{ bottom: "20%", right: "-10%", width: "50%", transform: "rotate(-12deg)" }}
            />

            <div className="container mx-auto px-4 sm:px-6 lg:px-10 relative z-30">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    {/* Left: text */}
                    <div className="lg:col-span-7 animate-fade-up">
                        <div
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold/30 bg-gold/5 mb-6"
                            data-testid="hero-badge"
                        >
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-gold" />
                            </span>
                            <span className="font-manrope text-[11px] uppercase tracking-[0.25em] text-gold">
                                Coaching online & in presenza
                            </span>
                        </div>

                        <h1
                            data-testid="hero-title"
                            className="font-oswald font-bold uppercase leading-[0.95] tracking-tight text-4xl sm:text-6xl lg:text-7xl xl:text-[96px] text-white"
                        >
                            Il tuo fisico.
                            <br />
                            <span className="text-shimmer">La tua forma migliore.</span>
                            <br />
                            Senza compromessi.
                        </h1>

                        <p
                            data-testid="hero-subtitle"
                            className="mt-6 sm:mt-8 max-w-xl font-manrope text-base sm:text-lg text-zinc-300 leading-relaxed"
                        >
                            RPF Coaching — il team guidato da Manuel Romano che trasforma
                            allenamento, calisthenics e nutrizione sportiva in un percorso
                            serio, professionale e costruito su misura per te.
                        </p>

                        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-4">
                            <a
                                href="#contatti"
                                data-testid="hero-cta-primary"
                                className="group inline-flex items-center justify-center gap-2 bg-gold text-ink font-oswald font-bold uppercase tracking-wider px-8 py-4 rounded hover:bg-gold-bright hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-all duration-300"
                            >
                                Inizia il tuo percorso
                                <ArrowRight
                                    size={18}
                                    className="transition-transform group-hover:translate-x-1"
                                />
                            </a>
                            <a
                                href="#servizi"
                                data-testid="hero-cta-secondary"
                                className="inline-flex items-center justify-center gap-2 bg-transparent text-white font-oswald font-bold uppercase tracking-wider px-8 py-4 rounded border border-gold/40 hover:bg-gold hover:text-ink hover:border-gold transition-all duration-300"
                            >
                                <Dumbbell size={18} />
                                Scopri i servizi
                            </a>
                        </div>

                        {/* stats */}
                        <div
                            className="mt-14 grid grid-cols-3 gap-4 max-w-xl"
                            data-testid="hero-stats"
                        >
                            {[
                                { k: "100+", v: "Atleti seguiti" },
                                { k: "7/7", v: "Supporto diretto" },
                                { k: "100%", v: "Piani su misura" },
                            ].map((s) => (
                                <div
                                    key={s.v}
                                    className="border-l-2 border-gold/60 pl-4"
                                >
                                    <div className="font-oswald text-3xl sm:text-4xl font-bold text-white">
                                        {s.k}
                                    </div>
                                    <div className="font-manrope text-xs uppercase tracking-widest text-zinc-400 mt-1">
                                        {s.v}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: logo */}
                    <div className="lg:col-span-5 relative hidden lg:block">
                        <div className="relative aspect-square max-w-[480px] mx-auto">
                            <div className="absolute inset-6 rounded-full bg-gold/10 blur-3xl animate-pulse" />
                            <div className="absolute inset-0 rounded-2xl border border-gold/20 p-4">
                                <img
                                    src={LOGO_URL}
                                    alt="RPF Coaching Logo"
                                    data-testid="hero-logo"
                                    className="w-full h-full object-cover rounded-xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* scroll indicator */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30">
                <div className="w-[1px] h-12 bg-gradient-to-b from-transparent via-gold to-transparent" />
            </div>
        </section>
    );
}
